import {selectDeviceAuthToken, selectIotDevice, selectIotDeviceAssetId} from '../../state/selectors/iotDevices';
import {selectRetailAuthToken} from '../../state/selectors/retail';
import {selectConsentsFlagsValues} from '../../state/selectors/session';
import {setDeviceAuthToken, updateDeviceData} from '../../state/slice/iotDevices';
import {dispatch, getState} from '../../state/store';
import helpers from '../../utils/helpers';
import retailService from '../retail/retailService';

const ASSETS_CLAIM = 'assets';
const EXPIRATION_KEY = 'exp';

export const nowSec = () => Math.floor(Date.now() / 1000);

export const extractExpirationTimeFromToken = (token) => {
    if (!token) return 0;
    try {
        const payload = helpers.parseJwt(token) || {};

        return Number(payload[EXPIRATION_KEY]) || 0;
    } catch {
        return 0;
    }
};

export const dispatchAssetsFromToken = (token, assetsClaim) => {
    const parsed = helpers.parseJwt(token) || {};
    const assets = assetsClaim ? parsed[assetsClaim] : {};

    for (const id of Object.keys(assets)) {
        const {serialNumber: sn} = assets[id] || {};

        if (sn) dispatch(updateDeviceData(sn, {assetId: id}));
    }
};

export const deleteUserAuthToken = (serialNumber) => {
    dispatch(setDeviceAuthToken(serialNumber, null));
};

const getNewRetailToken = async (codentify) => {
    const token = await retailService.getRetailAuthToken(codentify);

    return token || null;
};

const getStoredToken = (serialNumber) => {
    const device = selectIotDevice(serialNumber)(getState());
    const retailToken = selectRetailAuthToken(device?.codentify)(getState());

    return retailToken || selectDeviceAuthToken(serialNumber)(getState());
};

export const getBaseNewAuthToken = async (serialNumber, assetsClaim) => {
    const device = selectIotDevice(serialNumber)(getState());
    const retailToken = selectRetailAuthToken(device?.codentify)(getState());

    if (retailToken) return getNewRetailToken(device?.codentify);

    const consents = selectConsentsFlagsValues(getState());
    const assetId = selectIotDeviceAssetId(serialNumber)(getState());

    if (!assetId) return null;

    // lazy import to break circular dependency:
    // damClient → damTokenService → damClientService → uamfClientService → uamfClient → damClient
    const {default: damClientService} = await import('./damClientService');
    const token = await damClientService.postAuthToken(serialNumber, assetId, consents);

    if (!token) return null;

    dispatch(setDeviceAuthToken(serialNumber, token));
    dispatchAssetsFromToken(token, assetsClaim);

    return token;
};

const pendingTokens = {};

const getUserAuthToken = async (serialNumber) => {
    const stored = getStoredToken(serialNumber);

    if (pendingTokens[serialNumber]) {
        return pendingTokens[serialNumber];
    }

    if (stored && extractExpirationTimeFromToken(stored) > nowSec()) {
        return stored;
    }

    const fetchToken = async () => {
        const token = await getBaseNewAuthToken(serialNumber, ASSETS_CLAIM);

        if (extractExpirationTimeFromToken(token) <= nowSec()) {
            throw new Error('Received expired token');
        }

        return token;
    };

    pendingTokens[serialNumber] = fetchToken().finally(() => {
        delete pendingTokens[serialNumber];
    });

    return pendingTokens[serialNumber];
};

export default {getUserAuthToken, deleteUserAuthToken};
